import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronDown,
  LogOut,
  Menu,
  X,
  User,
  EyeOff,
  Eye,
} from 'lucide-react';
import { navigationGroups } from './nav-registry';
import type { NavGroup, NavItem } from './nav-registry';
import { composeNav } from './nav-compose';
import { CommandPalette } from '@/components/palette/CommandPalette';
import { useCommandPaletteShortcut } from '@/components/palette/useCommandPalette';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import type { ResolvedProfile, ResolvedRoleUi } from '@efacture/core/sector';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { isDesktopApp } from '@/lib/desktop';
import { quoteWording } from '@/lib/quote-wording';
import { useBillingState } from '@/api/queries/billing';
import { useAccountingSystem } from '@/api/queries/smt';
import type { JurisdictionSurfaces } from '@/api/queries/jurisdiction';
import { useSignaturesEnDoute } from '@efacture/web-billing/api/queries/mcf-signatures';
import brandLogo from '@/assets/brand-logo.png';

// ---------------------------------------------------------------
// Navigation Configuration
// ---------------------------------------------------------------

/** Ties the header burger to the drawer it opens. */
const MOBILE_NAV_ID = 'mobile-navigation';

function groupContainsPath(group: NavGroup, pathname: string): boolean {
  return group.items.some(
    (item) =>
      !!item.href &&
      (pathname === item.href || pathname.startsWith(item.href + '/'))
  );
}

// ---------------------------------------------------------------
// User Dropdown Component
// ---------------------------------------------------------------

interface UserDropdownProps {
  collapsed?: boolean | undefined;
  onMobileClose?: (() => void) | undefined;
  /** How many items the user hid from their menu. */
  hiddenCount?: number;
  /** Shows every hidden item again; offered while `hiddenCount` > 0. */
  onShowHidden?: (() => void) | undefined;
}

function UserDropdown({ collapsed = false, onMobileClose, hiddenCount = 0, onShowHidden }: UserDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsOpen(false);
    onMobileClose?.();
    navigate('/profile');
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    logout();
  };

  const showHidden =
    onShowHidden && hiddenCount > 0 ? (
      <button
        onClick={() => {
          setIsOpen(false);
          onShowHidden();
        }}
        role="menuitem"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
      >
        <Eye className="w-4 h-4" />
        {t('nav.showHidden').replace('{count}', String(hiddenCount))}
      </button>
    ) : null;

  if (!user) return null;

  const initials = user.firstName?.charAt(0).toUpperCase() || 'U';
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Utilisateur';

  if (collapsed) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Menu utilisateur"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-lagune-700 to-brand-lagune-800 flex items-center justify-center text-white font-semibold text-sm hover:from-brand-lagune-800 hover:to-brand-lagune-900 transition-colors"
        >
          {initials}
        </button>

        {isOpen && (
          <div role="menu" className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-medium text-slate-900 truncate">{fullName}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
            <div className="p-1">
              <button
                onClick={handleProfileClick}
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <User className="w-4 h-4" />
                Mon profil
              </button>
              {showHidden}
              <button
                onClick={handleLogoutClick}
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('common.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Menu utilisateur"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-lagune-700 to-brand-lagune-800 flex items-center justify-center text-white font-semibold text-sm shrink-0">
          {initials}
        </div>
        <div className="flex-1 text-left overflow-hidden">
          <p className="text-sm font-medium text-white truncate">{fullName}</p>
          <p className="text-xs text-brand-lagune-100/70 truncate">{user.email}</p>
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-brand-lagune-200/60 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div role="menu" className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          <div className="p-1">
            <button
              onClick={handleProfileClick}
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <User className="w-4 h-4" />
              Mon profil
            </button>
            {showHidden}
            <button
              onClick={handleLogoutClick}
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Sidebar Component
// ---------------------------------------------------------------

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /**
   * Opens the AI chat, seeded with a question when one is handed over.
   * Resolved by the caller for the same reason as `profile` below: this
   * component renders bare in several tests, with no chat provider above it.
   */
  onOpenChat?: (question?: string) => void;
  /**
   * Resolved by the caller (AppLayout), not fetched here: Sidebar is
   * rendered bare in several tests with no QueryClientProvider in the
   * tree, so a `useQuery` call inside this component would throw before
   * the no-op branch below is ever reached. Absent, exactly like `null`,
   * means the profile term hides nothing.
   */
  profile?: ResolvedProfile | null;
  /**
   * The role axis, resolved by the caller from the roles the user holds.
   *
   * Presentation only: it hoists groups and expands sections, over a menu
   * the four filtering terms have already composed. Absent means no
   * emphasis — registry order, everything collapsed (I-RP2).
   */
  roleUi?: ResolvedRoleUi | null;
  /**
   * What the company's jurisdiction provides, resolved by the caller for the
   * same reason as `profile`. Absent, exactly like `null`, hides nothing.
   */
  jurisdictionSurfaces?: JurisdictionSurfaces | null;
  /**
   * The keys the company switched off, resolved by the caller for the same
   * reason as `profile`. Absent, exactly like `null`, hides nothing.
   */
  companyDisabled?: readonly string[] | null;
  /** The keys the user hid from their own menu, resolved by the caller. */
  userHidden?: readonly string[];
  /** Hides an item from the user's own menu; without it no item offers it. */
  onHideItem?: (key: string) => void;
  /** Shows every hidden item again. */
  onShowHidden?: () => void;
}

export function Sidebar({
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
  onOpenChat,
  profile = null,
  roleUi = null,
  jurisdictionSurfaces = null,
  companyDisabled = null,
  userHidden = [],
  onHideItem,
  onShowHidden,
}: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { tenant } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Sections repliables : ouvertes par défaut si la route courante s'y trouve
  // (b) réorg menu 2026-07-18 : TOUT est replié par défaut, sauf le domaine
  // contenant la route active. À la navigation, le domaine de la nouvelle
  // route s'ouvre ; les sections ouvertes/fermées à la main sont conservées.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    // The role's sections start expanded, alongside the one holding the
    // current route. Seeded here rather than in an effect: an effect runs
    // after commit, so the first paint would show them collapsed.
    const openByRole = roleUi?.defaultOpenGroups ?? [];
    for (const group of navigationGroups) {
      if (group.collapsible) {
        initial[group.key] =
          groupContainsPath(group, location.pathname) ||
          openByRole.includes(group.key);
      }
    }
    return initial;
  });

  useEffect(() => {
    for (const group of navigationGroups) {
      if (group.collapsible && groupContainsPath(group, location.pathname)) {
        setOpenGroups((prev) => (prev[group.key] ? prev : { ...prev, [group.key]: true }));
      }
    }
  }, [location.pathname]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Plan effectif du tenant — FAIL-OPEN : sans billing:read (403) ou sur
  // desktop embarqué (billing neutralisé), features = null et rien n'est
  // masqué par le plan.
  const { data: billingState } = useBillingState({
    enabled: hasPermission('billing:read'),
  });
  const planFeatures = billingState?.features ?? null;

  // Régime comptable — SMT et comptabilité NORMALE sont EXCLUSIFS : la
  // section « Comptabilité » disparaît au profit de « Comptabilité
  // simplifiée » quand le tenant est au SMT, et inversement. Le régime
  // SYCEBNL (verticale ONG §2.1) conserve la section Comptabilité
  // normale mais masque les entrées SYSCOHADA-only (liasse fiscale
  // eSINTAX) — la TVA reste visible (assujettissement indépendant du
  // régime). Inconnu (pas accounting:read) = comportement NORMAL.
  const { data: accountingSystemData } = useAccountingSystem({
    enabled: hasPermission('accounting:read'),
  });
  const accountingSystem = accountingSystemData?.accountingSystem ?? null;

  // ★ Signatures MCF en doute — l'entrée n'existe QUE s'il y a quelque
  // chose à arbitrer. Interrogée uniquement pour un porteur de la
  // permission (un caissier ne déclenche aucune requête), et FAIL-CLOSED
  // à l'inverse de `planFeature` : requête en vol, 403 ou dossier sain
  // ⇒ `0` ⇒ rien n'apparaît. C'est ce qui garantit qu'un dossier sans
  // divergence ne voit strictement AUCUN changement d'écran.
  const { data: mcfEnDoute } = useSignaturesEnDoute({
    enabled: hasPermission('mcf:arbitrer-signature'),
  });
  const mcfEnDouteCount = mcfEnDoute?.signatures.length ?? 0;

  // The sector profile is a fourth, independent filtering term, resolved
  // by the caller and passed in as `profile` (see SidebarProps). The role
  // adds no term at all: it only orders what comes out.
  // The four filtering terms, resolved ONCE and shared with the command
  // palette: a palette computing its own would offer pages the menu hides —
  // the plan-gated ones, since `planFeatures: null` means « no gating ».
  // The company's switch-off applies to both; the user's hidden items only
  // to the menu, so the palette still reaches them.
  const navInput = {
    profile,
    planFeatures,
    accountingSystem,
    mcfEnDouteCount,
    isDesktop: isDesktopApp(),
    surfaces: jurisdictionSurfaces,
    groupOrder: roleUi?.groupOrder ?? [],
    companyDisabled,
  };

  const filteredGroups = composeNav({
    ...navInput,
    userHidden,
    hasPermission,
    hasAnyPermission,
  });

  // ⌘K opens the palette, ⌘J the AI chat (decision 2026-09-04). Mounted here
  // because this is where the nav terms already are.
  useCommandPaletteShortcut(
    useCallback(() => setPaletteOpen(true), []),
    onOpenChat
  );

  // The mobile drawer covers the page like a dialog and is trapped like one;
  // it keeps its navigation landmark role.
  const drawerRef = useDialogA11y<HTMLElement>({
    open: mobileOpen,
    onClose: () => onMobileClose?.(),
  });

  // Above the `lg` breakpoint the drawer is `display: none`. Left open across
  // a resize, its trap would hold Tab on controls that cannot take focus.
  useEffect(() => {
    if (!mobileOpen || typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeIfDesktop = () => {
      if (desktop.matches) onMobileClose?.();
    };
    closeIfDesktop();
    desktop.addEventListener('change', closeIfDesktop);
    return () => desktop.removeEventListener('change', closeIfDesktop);
  }, [mobileOpen, onMobileClose]);

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    if (item.heading) {
      if (collapsed) return null;
      return (
        <h3 className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-brand-lagune-100/70">
          {t(item.labelKey as any)}
        </h3>
      );
    }
    if (!item.href || !item.icon) return null;
    const isActive = item.exact
      ? location.pathname === item.href
      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    const label = item.key === 'sales-quotes' && isDesktopApp() ? quoteWording(true).menu : t(item.labelKey as any);
    // Collapsed, the icon is the whole link: the label becomes its accessible
    // name and the native tooltip.
    const collapsedLabel =
      item.badge && item.badge > 0 ? `${label} (${item.badge})` : label;

    const link = (
      <NavLink
        to={item.href}
        onClick={onMobileClose}
        {...(collapsed ? { 'aria-label': collapsedLabel, title: collapsedLabel } : {})}
        className={cn(
          'group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
          'hover:bg-white/10',
          isActive
            ? 'bg-gradient-to-r from-brand-turquoise/20 to-brand-turquoise/10 text-white shadow-sm'
            : 'text-brand-lagune-100/70 hover:text-white'
        )}
      >
        <item.icon
          className={cn(
            'w-5 h-5 flex-shrink-0 transition-colors',
            isActive ? 'text-white' : 'text-brand-lagune-200/60 group-hover:text-brand-lagune-100'
          )}
        />
        {!collapsed && (
          <span className="text-sm font-medium truncate">{label}</span>
        )}
        {!collapsed && item.badge && item.badge > 0 && (
          <span className="ml-auto bg-brand-turquoise text-brand-encre text-xs font-bold px-2 py-0.5 rounded-full">
            {item.badge}
          </span>
        )}
      </NavLink>
    );

    if (collapsed || !onHideItem) return link;
    const hideLabel = t('nav.hideItem').replace('{label}', label);
    return (
      <div className="group/nav-item relative">
        {link}
        {/* Not displayed until the row is hovered (pointer devices only) or
            holds the keyboard focus: an invisible button would take a tap. */}
        <button
          type="button"
          onClick={() => onHideItem(item.key)}
          aria-label={hideLabel}
          title={t('nav.hideItemShort')}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-brand-lagune-200/60 hover:text-white hover:bg-white/10 hidden group-hover/nav-item:block group-focus-within/nav-item:block focus-visible:block"
        >
          <EyeOff className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className={cn('p-4 flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
        <img src={brandLogo} alt={t('brand.name')} className="w-10 h-10 rounded-xl shadow-lg flex-shrink-0" />
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="text-lg font-bold text-white truncate">{t('brand.name')}</h1>
            <p className="text-xs text-brand-lagune-100/70 truncate">{tenant?.name || t('brand.baseline')}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 px-3 py-4 space-y-6 overflow-y-auto overscroll-contain">
        {filteredGroups.map((group, groupIndex) => {
          if (group.collapsible && !collapsed) {
            const isOpen = openGroups[group.key] ?? false;
            const isSectionActive = group.items.some(
              (item) =>
                !!item.href &&
                (location.pathname === item.href ||
                  location.pathname.startsWith(item.href + '/'))
            );
            const GroupIcon = group.icon;
            return (
              <div key={group.key}>
                <button
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={isOpen}
                  className={cn(
                    'w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
                    'hover:bg-white/10',
                    isSectionActive
                      ? 'text-white'
                      : 'text-brand-lagune-100/70 hover:text-white'
                  )}
                >
                  {GroupIcon && (
                    <GroupIcon
                      className={cn(
                        'w-5 h-5 flex-shrink-0 transition-colors',
                        isSectionActive
                          ? 'text-white'
                          : 'text-brand-lagune-200/60 group-hover:text-brand-lagune-100'
                      )}
                    />
                  )}
                  <span className="text-sm font-medium truncate flex-1 text-left">
                    {group.labelKey ? t(group.labelKey as any) : ''}
                  </span>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-brand-lagune-200/60 transition-transform',
                      isOpen && 'rotate-180'
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="mt-1 ml-4 pl-3 border-l border-brand-lagune-700/50 space-y-1">
                    {group.items.map((item) => (
                      <NavItemComponent key={item.key} item={item} />
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={group.key}>
              {group.labelKey && !collapsed && groupIndex > 0 && (
                <h2 className="px-3 mb-2 text-xs font-semibold text-brand-lagune-100/70 uppercase tracking-wider">
                  {t(group.labelKey as any)}
                </h2>
              )}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavItemComponent key={item.key} item={item} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User Section */}
      <div className={cn('p-4 border-t border-brand-lagune-700/50', collapsed && 'flex flex-col items-center')}>
        <UserDropdown
          collapsed={collapsed}
          onMobileClose={onMobileClose}
          hiddenCount={userHidden.length}
          onShowHidden={onShowHidden}
        />
        {!collapsed && (
          <p className="mt-2 text-[10px] text-brand-lagune-100/70 text-center">
            <Link
              to="/a-propos"
              onClick={onMobileClose}
              aria-label={`${t('about.title')} — ${t('about.version')} ${__APP_VERSION__}`}
              className="hover:text-white hover:underline"
            >
              v{__APP_VERSION__}
            </Link>
          </p>
        )}
      </div>

      {/* Collapse Toggle (Desktop) */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Déplier le menu' : 'Réduire le menu'}
          title={collapsed ? 'Déplier le menu' : 'Réduire le menu'}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-20 w-6 h-6 bg-brand-lagune-700 border border-brand-lagune-600 rounded-full flex items-center justify-center text-brand-lagune-200 hover:text-white hover:bg-brand-lagune-600 transition-colors shadow-md hidden lg:flex"
        >
          <ChevronLeft className={cn('w-4 h-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      )}
    </>
  );

  return (
    <>
      {/* Desktop Sidebar: from below the top banners (`--app-top-offset`,
          set by the layout) to the bottom of the viewport. */}
      <aside
        role="navigation"
        aria-label="Navigation principale"
        className={cn(
          'hidden lg:flex flex-col fixed left-0 top-[var(--app-top-offset,0px)] bottom-0 z-40',
          'bg-gradient-to-b from-brand-lagune-800 via-brand-lagune-900 to-brand-lagune-950',
          'border-r border-brand-lagune-700/50 transition-[width] duration-300',
          collapsed ? 'w-[72px]' : 'w-64'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Overlay and drawer stack above the network status banner (z-60),
          which would otherwise cover the drawer's close button. */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[61] backdrop-blur-sm"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        ref={drawerRef}
        id={MOBILE_NAV_ID}
        role="navigation"
        aria-label="Navigation mobile"
        // Slid off-screen, not unmounted: `inert` keeps its links out of the
        // tab order and the accessibility tree while closed.
        inert={!mobileOpen}
        // A column from below the top banners to the bottom of the dynamic
        // viewport: the menu scrolls inside it, and the account menu stays
        // pinned under it, clear of the notch and the home indicator. A banner
        // already covers the top safe area; the drawer pads what is left of it.
        className={cn(
          'lg:hidden fixed left-0 top-[var(--app-top-offset,0px)] h-[calc(100dvh-var(--app-top-offset,0px))] w-72 max-w-[85vw] z-[62] flex flex-col',
          'pt-[max(0px,env(safe-area-inset-top)-var(--app-top-offset,0px))] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]',
          'bg-gradient-to-b from-brand-lagune-800 via-brand-lagune-900 to-brand-lagune-950',
          'border-r border-brand-lagune-700/50 transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Mobile Close Button */}
        <button
          onClick={onMobileClose}
          aria-label="Fermer le menu"
          className="absolute right-3 top-[calc(1rem+max(0px,env(safe-area-inset-top)-var(--app-top-offset,0px)))] z-10 p-2 text-brand-lagune-200/70 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        {sidebarContent}
      </aside>

      {/* Mounted only while open: closed, it would still hold a search
          subscription and a key listener for a dialog nobody can see. */}
      {paletteOpen && (
        <CommandPalette
          open
          onClose={() => setPaletteOpen(false)}
          onAskAi={onOpenChat}
          navInput={navInput}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------
// Mobile Menu Button
// ---------------------------------------------------------------

interface MobileMenuButtonProps {
  onClick: () => void;
  expanded?: boolean;
}

export function MobileMenuButton({ onClick, expanded = false }: MobileMenuButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Ouvrir le menu"
      aria-expanded={expanded}
      aria-controls={MOBILE_NAV_ID}
      className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
    >
      <Menu className="w-6 h-6" />
    </button>
  );
}

export default Sidebar;
